import React, { useEffect, useState } from "react";
import "./SelectPaymentMethod.scss";
import { Modal, Switch } from "@mui/material";
import LockIcon2 from "../../../../components/icons/LockIcon2";

interface Props {
  onBack: () => void;
  onConfirmPayment: (
    useWalletPoints: boolean,
    paymentMethod: "agency" | "agent",
  ) => void;
  isAgencyPaymentAllowed: boolean;
  propertyAddress: string;
  selectedPlan: {
    planName: string;
    price: number;
    currency: string;
  };
  walletBalance: number;
  isFreeTrial: boolean;
  usageCount?: number;
  trialLimit?: number;
  discountPrice?: number;
  discountLimit?: number;
}

const SelectPaymentMethod: React.FC<Props> = ({
  onBack,
  onConfirmPayment,
  propertyAddress,
  selectedPlan,
  walletBalance,
  isFreeTrial,
  usageCount = 0,
  trialLimit = 0,
  discountPrice = 0,
  discountLimit = 0,
  isAgencyPaymentAllowed,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [useWalletPoints, setUseWalletPoints] = useState<boolean>(false);

  const [paymentMethod, setPaymentMethod] = useState<"agency" | "agent">(
    isAgencyPaymentAllowed ? "agency" : "agent",
  );

  const isDiscountApplied =
    !isFreeTrial && discountLimit > 0 && usageCount < discountLimit;

  const currentDisplayPrice =
    isDiscountApplied && discountPrice > 0 ? discountPrice : selectedPlan.price;

  const handlePayment = () => {
    setIsLoading(true);
    if (isFreeTrial) {
      onConfirmPayment(false, paymentMethod);
    } else {
      onConfirmPayment(useWalletPoints, paymentMethod);
    }
  };

  const safeWalletBalance = Number.isFinite(walletBalance) ? walletBalance : 0;

  const redeemablePoints = Math.min(safeWalletBalance, currentDisplayPrice);

  const remainingPoints = safeWalletBalance - redeemablePoints;

  const currentNumber = usageCount + 1;

  const getOrdinal = (n: number) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  useEffect(() => {
    if (!isAgencyPaymentAllowed) {
      setPaymentMethod("agent");
    } else {
      setPaymentMethod("agency");
    }
  }, [isAgencyPaymentAllowed]);

  return (
    <div className="select-payment-method-container">
      <Modal
        open={isLoading}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setIsLoading(false);
          }
        }}
        aria-labelledby="processing-modal"
        className="add-property-modal styled-modal"
        disableEscapeKeyDown
      >
        <div className="modal-center-wrapper">
          <div className="loading-content-wrapper">
            <div className="loading-container">
              <div className="spinner" />
              <p className="loading-text font-inter-500">
                Processing your request
                <span className="desktop-break">
                  <br />
                </span>
                Please wait...
              </p>
            </div>
          </div>
        </div>
      </Modal>

      <div className="header-section">
        <p className="context-label">Purchasing property marketing plan for</p>
        <p className="address-link">{propertyAddress}</p>
      </div>

      <div className="cards-wrapper">
        <div className="card plan-details-card">
          <div className="card-content">
            <p className="plan-name font-inter-600">{selectedPlan.planName}</p>
            <p className="sub-type">Property marketing</p>
            {isFreeTrial ? (
              <div className="price-container">
                <p
                  className="gst-note font-inter-400"
                  style={{
                    textDecoration: "line-through",
                    fontSize: "20px",
                    marginBottom: "6px",
                    color: "#4A4A4A",
                  }}
                >
                  {selectedPlan.currency} ${selectedPlan.price.toFixed(2)}
                </p>
                <h1
                  className="price font-inter-600"
                  style={{
                    color: "#1A1A1A",
                    marginBottom: "6px",
                    fontSize: "40px",
                  }}
                >
                  {selectedPlan.currency} $0.00
                </h1>
                <p
                  className="gst-note font-inter-400"
                  style={{
                    color: "#000000",
                    fontSize: "14px",
                  }}
                >
                  Claiming free trial for property ({currentNumber} of{" "}
                  {trialLimit}){/* Free Property Trial Applied */}
                  {/* <span
                    style={{
                      display: "block",
                      marginTop: "6px",
                      fontSize: "12px",
                    }}
                  >
                    ( {getOrdinal(currentNumber)} of {trialLimit} properties
                    free )
                  </span> */}
                </p>
              </div>
            ) : (
              <>
                {isDiscountApplied ? (
                  <div className="price-container">
                    <p
                      className="font-inter-400"
                      style={{
                        textDecoration: "line-through",
                        marginBottom: "6px",
                        fontSize: "20px",
                        color: "#4A4A4A",
                      }}
                    >
                      {selectedPlan.currency} {selectedPlan.price.toFixed(2)}
                    </p>

                    <h1 className="price font-inter-600">
                      {selectedPlan.currency} ${currentDisplayPrice.toFixed(2)}
                    </h1>

                    <p
                      className="gst-note font-inter-400"
                      style={{
                        marginTop: "4px",
                        marginBottom: "17px",
                        color: "#000000",
                        fontSize: "14px",
                      }}
                    >
                      Claiming offer for property ({usageCount + 1} of{" "}
                      {discountLimit})
                    </p>
                  </div>
                ) : (
                  <h1 className="price font-inter-600">
                    {selectedPlan.currency} {selectedPlan.price.toFixed(2)}
                  </h1>
                )}
                <p className="gst-note font-inter-400">* Price excludes GST</p>
              </>
            )}
          </div>
        </div>

        <div
          className={`card wallet-card ${
            isFreeTrial || currentDisplayPrice <= 0 ? "disabled-wallet" : ""
          }`}
        >
          <div className="wallet-flex-container">
            {/* <div className="icon-wrapper">
              <img
                src="/images/wallet.svg"
                alt="Wallet"
                className="wallet-icon"
              />
            </div> */}

            <div className="text-group">
              <div className="title-row">
                <h3>Available Points: {walletBalance}</h3>
                <div className="wallet-switch">
                  <input
                    type="checkbox"
                    id="walletToggle"
                    checked={useWalletPoints}
                    onChange={(e) => setUseWalletPoints(e.target.checked)}
                    disabled={
                      walletBalance <= 0 ||
                      isFreeTrial ||
                      currentDisplayPrice <= 0
                    }
                  />
                  <label htmlFor="walletToggle" />
                </div>
              </div>
              <p className="description">
                Reduces ex-GST invoice price by $1.00 {selectedPlan.currency}{" "}
                per point redeemed.
              </p>
            </div>
          </div>

          <p className="toggle-instruction">
            {!useWalletPoints ? (
              "Toggle to redeem points for this purchase."
            ) : (
              <>
                <p className="wallet-points-redeem">
                  Redeeming {redeemablePoints} points
                </p>
                <br />
                <p className="wallet-points-updated">
                  New Balance after purchase: {remainingPoints} points
                </p>
              </>
            )}
          </p>
        </div>
      </div>

      <div className="payment-method-section">
        <h3 className="payment-title font-inter-400">Payment Method</h3>
        <p className="payment-subtitle font-inter-400">
          Select payment method for this purchase.
        </p>

        <div className="payment-options">
          <button
            className={`payment-option font-inter-500 ${
              paymentMethod === "agency" ? "active" : ""
            } ${!isAgencyPaymentAllowed ? "locked" : ""}`}
            disabled={!isAgencyPaymentAllowed}
            onClick={() => isAgencyPaymentAllowed && setPaymentMethod("agency")}
          >
            {!isAgencyPaymentAllowed && <LockIcon2 />}
            Pay via Agency Payment Method
          </button>

          <button
            className={`payment-option font-inter-500 ${
              paymentMethod === "agent" ? "active" : ""
            }`}
            onClick={() => setPaymentMethod("agent")}
          >
            Pay via Agent Payment Method
          </button>
        </div>
      </div>

      <div className="action-footer">
        <button
          className="pay-button font-inter-500"
          onClick={handlePayment}
          disabled={isLoading}
        >
          Continue to Payment
        </button>

        <div className="footer-terms">
          <p className="agree-text font-inter-400">
            By proceeding, you agree to our{" "}
            <a href="/" className="policy-link">
              Cancellation and refund policy.
            </a>
          </p>
          <p className="disclaimer-text font-inter-400">
            Please note that while we do not charge refund fees, original
            transaction fees are non-refundable.
            <br />
            Cancellations or refunds are not possible once our services have
            been accessed or used.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SelectPaymentMethod;
